import getOpenHousesFromRepliersListing from '../helpers/repliersData/getOpenHousesFromRepliersListing';
import Address, { AddressObject } from './Address';
import GpsCoordinates from './GpsCoordinates';
import { ListingRecordDetails } from './ListingRecordDetails';
import { OpenHouse, OpenHouseObject } from './OpenHouse';
import { RepliersListing } from './repliers/RepliersListing';
import { RepliersListingStatus } from './repliers/RepliersListingStatus';
import { DateTime } from 'luxon';
import { RepliersListingTransactionType } from './repliers/RepliersListingTransactionType';
import { RepliersListingLastStatus } from './repliers/RepliersListingLastStatus';
import { RepliersListingTaxes } from './repliers/RepliersListingTaxes';
import '../jsTypeExtensions/numberAsPriceExtension';
import { RepliersListingBuildingClass } from './repliers/RepliersListingBuildingClass';
import { ListingDetails, ListingDetailsObject } from './ListingDetails';
import isEmpty from '../helpers/isEmpty';
import { instanceToPlain } from 'class-transformer';
import { ListingLot, ListingLotObject } from './ListingLot';
import {
  PROPERTY_TYPE_DISPLAY_MAPPING,
  RepliersListingPropertyType,
} from './repliers/RepliersListingPropertyType';
import { SimpleListing } from './SimpleListing';
import round from '../helpers/round';
import {
  ListingCondoDetails,
  ListingCondoDetailsObject,
  ListingCondoDetailsRecord,
} from './ListingCondoDetails';
import metresToFeet from '../helpers/conversions/metresToFeet';
import { deepCloneWithUpdates } from '../utils/deepCloneWithUpdates';
import RecursivePartial from './RecursivePartial';
import orderRepliersImages from '../helpers/repliersData/orderRepliersImages';
import { convertToNullOrValidBoolean } from '../helpers/sanitization/convertToNullOrValidBoolean';
import { convertToNullOrValidNumber } from '../helpers/sanitization/convertToNullOrValidNumber';
import isDefinedAndNonNull from '../utils/isDefinedAndNonNull';
import {
  PropertyTypeFilterOptionLabels,
  propertyTypeFilterOptions,
} from './SearchFilters';
import { RepliersListingStyle } from './repliers/RepliersListingStyle';
import { RepliersListingBalcony } from './repliers/RepliersListingBalcony';
import { RepliersListingCondoExposure } from './repliers/RepliersListingCondoExposure';
import sanitizeRepliersPropertyType from '../helpers/repliersData/sanitizeRepliersPropertyType';
import sanitizeRepliersStyle from '../helpers/repliersData/sanitizeRepliersStyle';
import getDateBuiltRangeFromRepliersListing from '../helpers/repliersData/getDateBuiltRangeFromRepliersListing';
import getIsFsboFromRepliersListing from '../helpers/repliersData/getIsFsboFromRepliersListing';
import getSqftRangeFromRepliersListing from '../helpers/repliersData/getSqftRangeFromRepliersListing';
import getLastStatusFromRepliersListing from '../helpers/repliersData/getLastStatusFromRepliersListing';
import getTorontoDateTimeFromDate from '../utils/getTorontoDateTimeFromDate';
import getDateFromUtcDateTimeString from '../utils/getDateFromUtcDateTimeString';

export type UnpersistedFullListing = Omit<FullListing, 'id'>;

export type FullListingObject = {
  id?: string;
  propertyId?: string;
  mlsNumber: string;
  listDate: string;
  soldDate: null | string;
  address: AddressObject;
  coordinates: GpsCoordinates;
  resource: string;
  listPrice: number;
  soldPrice: null | number;
  originalPrice: null | number;
  lastStatus: null | RepliersListingLastStatus;
  status: RepliersListingStatus;
  images: string[];
  blurhashes?: null | string[];
  blurhashesUpdatedAt?: null | string;
  details: ListingDetailsObject;
  taxes: RepliersListingTaxes;
  brokerageName: string;
  transactionType: RepliersListingTransactionType;
  buildingClass: RepliersListingBuildingClass;
  propertyType: RepliersListingPropertyType;
  style: null | RepliersListingStyle;
  lot: ListingLotObject;
  timestamps: {
    expiryDate: null | undefined | string;
    terminatedDate: null | undefined | string;
    listingEntryDate: null | undefined | string;
    closedDate: null | undefined | string;
    possessionDate: null | undefined | string;
    idxUpdatedDate: null | undefined | string;
    conditionalExpiryDate: null | undefined | string;
    listingUpdatedDate: null | undefined | string;
    photosUpdatedDate: null | undefined | string;
    suspendedDate: null | undefined | string;
    extensionEntryDate: null | undefined | string;
    unavailableDate: null | undefined | string;
  };
  condo: null | ListingCondoDetailsObject;
  rooms: any;
  permissions: {
    canDisplayAddress: boolean;
    canDisplayPublic: boolean;
  };
  mlsUpdatedOn: string;
  daysOnMarketFromMls: null | number;
  repliersUpdatedOn: null | string;
  openHouses: null | Array<OpenHouseObject>;
  similarSolds: null | string[];
  similarSoldsUpdatedAt: null | string;
  offerDate: null | string;
  isOfferDateProcessed: boolean;
  offerCount: null | number;
  dateBuiltRange: null | string;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;
  sqftRange: null | string;
  lastGeocodedAt?: string;
};

export type FullListingRecord = {
  id: string;
  propertyId: string | null;
  mlsNumber: string;
  listDate: string;
  listPrice: number;
  soldPrice: null | number;
  soldDate: null | string;
  originalPrice: null | number;
  expiryDate: null | string;
  terminatedDate: null | string;
  listingEntryDate: null | string;
  closedDate: null | string;
  possessionDate: null | string;
  idxUpdatedDate: null | string;
  conditionalExpiryDate: null | string;
  listingUpdatedDate: null | string;
  photosUpdatedDate: null | string;
  suspendedDate: null | string;
  extensionEntryDate: null | string;
  unavailableDate: null | string;
  coordinates: GpsCoordinates;
  address: AddressObject;
  resource: string;
  lastStatus: null | RepliersListingLastStatus;
  status: RepliersListingStatus;
  images: string[];
  blurhashes: null | string[];
  blurhashesUpdatedAt: null | string;
  details: ListingRecordDetails;
  taxes: RepliersListingTaxes;
  brokerageName: string;
  transactionType: RepliersListingTransactionType;
  buildingClass: RepliersListingBuildingClass;
  lotDepth: number | null;
  lotWidth: number | null;
  lotDetails: {
    irregular: string | null;
    acres: string | null;
    legalDescription: string | null;
  };
  basement1: string | null;
  basement2: string | null;
  description: string | null;
  numBathrooms: number | null;
  numBathroomsPlus: number | null;
  numBedrooms: number | null;
  numBedroomsPlus: number | null;
  numGarageSpaces: number | null;
  numParkingSpaces: number | null;
  sqft: string | null;
  propertyType: RepliersListingPropertyType;
  style: null | RepliersListingStyle;
  balcony: null | RepliersListingBalcony;
  condoExposure: null | RepliersListingCondoExposure;
  condo: ListingCondoDetailsRecord;
  permissionsCanDisplayAddress: boolean;
  permissionsCanDisplayPublic: boolean;
  mlsUpdatedOn: string;
  daysOnMarketFromMls: number;
  rooms: {
    [key: number]: {
      description: string;
      features: string;
      features2: string;
      features3: string;
      length: string;
      width: string;
      level: string;
    };
  };
  repliersUpdatedOn: string | null;
  openHouses: Array<OpenHouseObject>;
  similarSolds: null | string[];
  similarSoldsUpdatedAt: null | string;
  brokerRemarks: string | null;
  offerDate: null | string;
  isOfferDateProcessed: boolean;
  offerCount: null | number;
  dateBuiltRange: null | string;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;
  sqftRange: null | string;
  lastGeocodedAt: null | string;
  // occupancy: string;
  // nearby: {
  //   ammenities: string[];
  // };
  // agents: any;
};

class FullListing extends SimpleListing {
  id?: string;
  propertyId: null | string;
  mlsNumber: string;
  listDate: DateTime;
  soldDate: null | DateTime;
  soldPrice: null | number;
  originalPrice: null | number;
  listPrice: number;
  coordinates: GpsCoordinates;
  address: Address;
  resource: string;
  lastStatus: null | RepliersListingLastStatus;
  status: RepliersListingStatus;
  blurhashes: null | string[];
  blurhashesUpdatedAt: null | DateTime;
  details: ListingDetails;
  taxes: RepliersListingTaxes;
  propertyType: RepliersListingPropertyType;
  style: null | RepliersListingStyle;
  lot: ListingLot;
  timestamps: {
    expiryDate: null | undefined | DateTime;
    terminatedDate: null | undefined | DateTime;
    listingEntryDate: null | undefined | DateTime;
    closedDate: null | undefined | DateTime;
    possessionDate: null | undefined | DateTime;
    idxUpdatedDate: null | undefined | DateTime;
    conditionalExpiryDate: null | undefined | DateTime;
    listingUpdatedDate: null | undefined | DateTime;
    photosUpdatedDate: null | undefined | DateTime;
    suspendedDate: null | undefined | DateTime;
    extensionEntryDate: null | undefined | DateTime;
    unavailableDate: null | undefined | DateTime;
  };
  condo: null | ListingCondoDetails;
  rooms: {
    [key: number]: {
      description: string;
      features: string;
      features2: string;
      features3: string;
      length: string;
      width: string;
      level: string;
    };
  };
  permissions: {
    canDisplayAddress: boolean;
    canDisplayPublic: boolean;
  };
  mlsUpdatedOn: DateTime;
  daysOnMarketFromMls: null | number;
  repliersUpdatedOn: null | DateTime;
  openHouses: null | Array<OpenHouse>;
  similarSolds: null | string[];
  similarSoldsUpdatedAt: null | DateTime;
  offerDate: null | DateTime;
  isOfferDateProcessed: boolean;
  offerCount: null | number;
  dateBuiltRange: null | string;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;
  sqftRange: null | string;
  lastGeocodedAt?: DateTime;
  // occupancy: string;
  // nearby: {
  //   ammenities: string[];
  // };
  // agents: any;

  static fromObject(listingObject: FullListingObject): FullListing {
    return new FullListing(listingObject);
  }

  static fromRepliersListing(
    repliersListing: RepliersListing,
  ): UnpersistedFullListing {
    const canDisplayAddress = convertToNullOrValidBoolean(
      repliersListing.permissions.displayAddressOnInternet,
    );
    const canDisplayPublic = convertToNullOrValidBoolean(
      repliersListing.permissions.displayPublic,
    );

    const torontoListDate = getTorontoDateTimeFromDate(
      getDateFromUtcDateTimeString(repliersListing.listDate),
    );

    return new FullListing({
      mlsNumber: repliersListing.mlsNumber,
      listDate: torontoListDate.toISO(),
      soldDate: repliersListing.soldDate,
      address: Address.fromRepliersAddress(
        repliersListing.address,
        repliersListing.resource,
      ).toObject(),
      coordinates: {
        latitude: Number(repliersListing.map.latitude),
        longitude: Number(repliersListing.map.longitude),
      },
      resource: repliersListing.resource,
      listPrice: Number(repliersListing.listPrice),
      soldPrice: Number(repliersListing.soldPrice),
      originalPrice: Number(repliersListing.originalPrice) || null,
      lastStatus: getLastStatusFromRepliersListing(repliersListing),
      status: repliersListing.status,
      images: orderRepliersImages(repliersListing.images),
      details: ListingDetails.fromRepliersListingDetails(
        repliersListing.details,
      ).toObject(),
      condo:
        ListingCondoDetails.fromRepliersListing(repliersListing)?.toObject() ||
        null,
      taxes: repliersListing.taxes,
      brokerageName: repliersListing.office.brokerageName,
      transactionType: repliersListing.type,
      buildingClass: repliersListing.class,
      propertyType: sanitizeRepliersPropertyType(repliersListing),
      style: sanitizeRepliersStyle(repliersListing),
      lot: ListingLot.fromRepliersListing(repliersListing).toObject(),
      timestamps: {
        expiryDate: repliersListing.timestamps.expiryDate,
        terminatedDate: repliersListing.timestamps.terminatedDate,
        listingEntryDate: repliersListing.timestamps.listingEntryDate,
        closedDate: repliersListing.timestamps.closedDate,
        possessionDate: repliersListing.timestamps.possessionDate,
        idxUpdatedDate: repliersListing.timestamps.idxUpdated,
        conditionalExpiryDate: repliersListing.timestamps.conditionalExpiryDate,
        listingUpdatedDate: repliersListing.timestamps.listingUpdated,
        photosUpdatedDate: repliersListing.timestamps.photosUpdated,
        suspendedDate: repliersListing.timestamps.suspendedDate,
        extensionEntryDate: repliersListing.timestamps.extensionEntryDate,
        unavailableDate: repliersListing.timestamps.unavailableDate,
      },
      rooms: repliersListing.rooms,
      permissions: {
        canDisplayAddress: isDefinedAndNonNull(canDisplayAddress)
          ? canDisplayAddress
          : true, // This should always be specified, but if it isn't, assume true
        canDisplayPublic: isDefinedAndNonNull(canDisplayPublic)
          ? canDisplayPublic
          : true, // This should always be specified, but if it isn't, assume true
      },
      mlsUpdatedOn: repliersListing.updatedOn,
      daysOnMarketFromMls: convertToNullOrValidNumber(
        repliersListing.daysOnMarket,
      ),
      repliersUpdatedOn: repliersListing.timestamps.repliersUpdatedOn || null,
      openHouses: getOpenHousesFromRepliersListing(repliersListing).map(
        openHouse => openHouse.toObject(),
      ),
      similarSolds: null,
      similarSoldsUpdatedAt: null,
      offerDate: null,
      isOfferDateProcessed: false,
      offerCount: null,
      dateBuiltRange: getDateBuiltRangeFromRepliersListing(repliersListing),
      isFsbo: getIsFsboFromRepliersListing(repliersListing),
      isPowerOfSale: null,
      sqftRange: getSqftRangeFromRepliersListing(repliersListing),
    });
  }

  static fromListingRecord(listingRecord: FullListingRecord): FullListing {
    return new FullListing({
      id: listingRecord.id,
      propertyId: listingRecord.propertyId,
      mlsNumber: listingRecord.mlsNumber,
      listDate: listingRecord.listDate,
      soldDate: listingRecord.soldDate,
      address: {
        area: listingRecord.address.area,
        zip: listingRecord.address.zip,
        country: listingRecord.address.country,
        city: listingRecord.address.city,
        streetNumber: listingRecord.address.streetNumber,
        unitNumber: listingRecord.address.unitNumber,
        streetDirection: listingRecord.address.streetDirection,
        streetName: listingRecord.address.streetName,
        district: listingRecord.address.district,
        streetSuffix: listingRecord.address.streetSuffix,
        neighborhood: listingRecord.address.neighborhood,
        state: listingRecord.address.state,
        majorIntersection: listingRecord.address.majorIntersection,
        communityCode: listingRecord.address.communityCode,
      },
      coordinates: listingRecord.coordinates,
      resource: listingRecord.resource,
      listPrice: listingRecord.listPrice,
      soldPrice: listingRecord.soldPrice,
      originalPrice: listingRecord.originalPrice,
      lastStatus: listingRecord.lastStatus,
      status: listingRecord.status,
      images: listingRecord.images,
      blurhashes: listingRecord.blurhashes || null,
      blurhashesUpdatedAt: listingRecord.blurhashesUpdatedAt || null,
      details: {
        ...listingRecord.details,
        basement1: listingRecord.basement1,
        basement2: listingRecord.basement2,
        description: listingRecord.description,
        numBathrooms: listingRecord.numBathrooms,
        numBathroomsPlus: listingRecord.numBathroomsPlus,
        numBedrooms: listingRecord.numBedrooms,
        numBedroomsPlus: listingRecord.numBedroomsPlus,
        numGarageSpaces: listingRecord.numGarageSpaces,
        numParkingSpaces: listingRecord.numParkingSpaces,
        sqft: listingRecord.sqft,
        balcony: listingRecord.balcony,
      },
      taxes: listingRecord.taxes,
      brokerageName: listingRecord.brokerageName,
      transactionType: listingRecord.transactionType,
      buildingClass: listingRecord.buildingClass,
      propertyType: listingRecord.propertyType,
      style: listingRecord.style,
      lot: ListingLot.fromListingRecord(listingRecord).toObject(),
      timestamps: {
        expiryDate: listingRecord.expiryDate,
        terminatedDate: listingRecord.terminatedDate,
        listingEntryDate: listingRecord.listingEntryDate,
        closedDate: listingRecord.closedDate,
        possessionDate: listingRecord.possessionDate,
        idxUpdatedDate: listingRecord.idxUpdatedDate,
        conditionalExpiryDate: listingRecord.conditionalExpiryDate,
        listingUpdatedDate: listingRecord.listingUpdatedDate,
        photosUpdatedDate: listingRecord.photosUpdatedDate,
        suspendedDate: listingRecord.suspendedDate,
        extensionEntryDate: listingRecord.extensionEntryDate,
        unavailableDate: listingRecord.unavailableDate,
      },
      condo:
        ListingCondoDetails.fromListingRecord(listingRecord)?.toObject() ||
        null,
      rooms: listingRecord.rooms,
      permissions: {
        canDisplayAddress: listingRecord.permissionsCanDisplayAddress,
        canDisplayPublic: listingRecord.permissionsCanDisplayPublic,
      },
      mlsUpdatedOn: listingRecord.mlsUpdatedOn,
      daysOnMarketFromMls: listingRecord.daysOnMarketFromMls,
      repliersUpdatedOn: listingRecord.repliersUpdatedOn,
      openHouses: listingRecord.openHouses,
      similarSolds: listingRecord.similarSolds,
      similarSoldsUpdatedAt: listingRecord.similarSoldsUpdatedAt,
      offerDate: listingRecord.offerDate,
      isOfferDateProcessed: listingRecord.isOfferDateProcessed,
      offerCount: listingRecord.offerCount,
      dateBuiltRange: listingRecord.dateBuiltRange,
      isFsbo: listingRecord.isFsbo,
      isPowerOfSale: listingRecord.isPowerOfSale,
      sqftRange: listingRecord.sqftRange,
      lastGeocodedAt: listingRecord.lastGeocodedAt,
    });
  }

  constructor({
    id,
    mlsNumber,
    listDate,
    soldDate,
    listPrice,
    soldPrice,
    originalPrice,
    images,
    blurhashes,
    blurhashesUpdatedAt,
    details,
    condo,
    address,
    brokerageName,
    status,
    lastStatus,
    transactionType,
    propertyId,
    coordinates,
    resource,
    taxes,
    buildingClass,
    propertyType,
    style,
    lot,
    timestamps,
    rooms,
    permissions,
    mlsUpdatedOn,
    daysOnMarketFromMls,
    repliersUpdatedOn,
    openHouses,
    similarSolds,
    similarSoldsUpdatedAt,
    offerDate,
    isOfferDateProcessed,
    offerCount,
    dateBuiltRange,
    isFsbo,
    isPowerOfSale,
    sqftRange,
    lastGeocodedAt,
  }: FullListingObject) {
    super({
      id,
      mlsNumber,
      listDate,
      soldDate,
      listPrice,
      soldPrice,
      images,
      blurhashes,
      details,
      address,
      coordinates,
      brokerageName,
      status,
      lastStatus,
      transactionType,
      buildingClass,
      permissions,
      daysOnMarketFromMls,
      mlsUpdatedOn,
      resource,
      openHouses,
      offerDate,
      offerCount,
      isFsbo,
      isPowerOfSale,
    });
    if (propertyId) {
      this.propertyId = propertyId;
    }
    this.resource = resource;
    this.blurhashesUpdatedAt = blurhashesUpdatedAt
      ? DateTime.fromISO(blurhashesUpdatedAt)
      : null;
    this.details = ListingDetails.fromObject(details);
    this.condo = condo ? ListingCondoDetails.fromObject(condo) : null;
    this.taxes = taxes;
    this.propertyType = propertyType;
    this.style = style;
    this.lot = new ListingLot(lot);
    this.timestamps = {
      expiryDate: timestamps.expiryDate
        ? DateTime.fromISO(timestamps.expiryDate)
        : null,
      terminatedDate: timestamps.terminatedDate
        ? DateTime.fromISO(timestamps.terminatedDate)
        : null,
      listingEntryDate: timestamps.listingEntryDate
        ? DateTime.fromISO(timestamps.listingEntryDate)
        : null,
      closedDate: timestamps.closedDate
        ? DateTime.fromISO(timestamps.closedDate)
        : null,
      possessionDate: timestamps.possessionDate
        ? DateTime.fromISO(timestamps.possessionDate)
        : null,
      idxUpdatedDate: timestamps.idxUpdatedDate
        ? DateTime.fromISO(timestamps.idxUpdatedDate)
        : null,
      conditionalExpiryDate: timestamps.conditionalExpiryDate
        ? DateTime.fromISO(timestamps.conditionalExpiryDate)
        : null,
      listingUpdatedDate: timestamps.listingUpdatedDate
        ? DateTime.fromISO(timestamps.listingUpdatedDate)
        : null,
      photosUpdatedDate: timestamps.photosUpdatedDate
        ? DateTime.fromISO(timestamps.photosUpdatedDate)
        : null,
      suspendedDate: timestamps.suspendedDate
        ? DateTime.fromISO(timestamps.suspendedDate)
        : null,
      extensionEntryDate: timestamps.extensionEntryDate
        ? DateTime.fromISO(timestamps.extensionEntryDate)
        : null,
      unavailableDate: timestamps.unavailableDate
        ? DateTime.fromISO(timestamps.unavailableDate)
        : null,
    };
    this.originalPrice = originalPrice;
    this.rooms = rooms;
    this.permissions = permissions;
    this.repliersUpdatedOn = repliersUpdatedOn
      ? DateTime.fromISO(repliersUpdatedOn)
      : null;
    this.similarSolds = similarSolds;
    this.similarSoldsUpdatedAt = similarSoldsUpdatedAt
      ? DateTime.fromISO(similarSoldsUpdatedAt)
      : null;
    this.isOfferDateProcessed = isOfferDateProcessed;
    this.dateBuiltRange = dateBuiltRange;
    this.sqftRange = sqftRange;
    this.lastGeocodedAt = lastGeocodedAt
      ? DateTime.fromISO(lastGeocodedAt)
      : null;
  }

  drivewayText(): string | null {
    if (!this.details.driveway) {
      return null;
    }

    if (this.details.driveway === 'None') {
      return 'No Driveway';
    } else if (this.details.driveway === 'Front Yard') {
      return 'Front Yard Pad';
    }

    return `${this.details.driveway} Driveway`;
  }

  hasParking(): boolean {
    return (
      Number(this.details.numParkingSpaces) > 0 ||
      Number(this.details.numGarageSpaces) > 0
    );
  }

  parkingSpacesText(): string {
    if (!this.hasParking()) {
      return `No Parking`;
    }

    let parkingText;

    if (this.details.numParkingSpaces) {
      parkingText = `${this.details.numParkingSpaces} Parking`;
    } else {
      parkingText = `${this.details.numGarageSpaces} Parking`;
    }

    return parkingText;
  }

  parkingDetailsText(): string | null {
    if (isEmpty(this.details.numGarageSpaces) && !this.drivewayText()) {
      return null;
    }

    let text = '';

    if (!isEmpty(this.details.numGarageSpaces)) {
      text = `${this.details.numGarageSpaces} Garage`;
    }

    if (this.drivewayText()) {
      if (text) {
        // add a separator
        text += ' | ';
      }
      text += this.drivewayText();
    }

    return text;
  }

  annualTaxTextHeader(): string | null {
    if (isEmpty(this.taxes.assessmentYear) || !this.annualTaxAmount())
      return `Taxes`;
    return `${this.taxes.assessmentYear} Taxes`;
  }

  annualTaxAmount(): number | null {
    const annualAmountString = this.taxes.annualAmount;
    if (isEmpty(annualAmountString) || isNaN(Number(annualAmountString))) {
      return null;
    }
    return Number(annualAmountString);
  }

  annualTaxText(): string | null {
    if (!this.annualTaxAmount()) return null;

    return `${this.annualTaxAmount().formatAsPrice()}`;
  }

  sewerText(): string | null {
    const sewer = this.details.sewer;
    if (isEmpty(sewer)) return null;

    if (sewer === 'None') return 'No Sewer System';
    if (sewer === 'Other') return 'Other Sewer System';

    return sewer;
  }

  hasCondoPool(): boolean {
    if (!this.condo) return false;
    return (
      this.condo.amenities.includes('Indoor Pool') ||
      this.condo.amenities.includes('Outdoor Pool') ||
      this.condo.amenities.includes('Lap Pool')
    );
  }

  hasResidentialPool(): boolean {
    return (
      isDefinedAndNonNull(this.details.swimmingPool) &&
      this.details.swimmingPool !== 'None'
    );
  }

  hasPool(): boolean {
    return this.hasCondoPool() || this.hasResidentialPool();
  }

  condoPoolText(): string {
    if (!this.hasCondoPool()) return 'No Pool';
    const poolAmenities = this.condo.amenities.filter(
      amenity =>
        amenity === 'Indoor Pool' ||
        amenity === 'Outdoor Pool' ||
        amenity === 'Lap Pool',
    );
    return poolAmenities.join(', ');
  }

  residentialPoolText(): string {
    const pool = this.details.swimmingPool;
    if (isEmpty(pool)) return 'No Pool';
    if (pool === 'None') return 'No Pool';
    if (pool === 'Abv Grnd') return 'Above Ground';
    return pool;
  }

  poolText(): string {
    if (this.hasCondoPool()) return this.condoPoolText();
    if (this.hasResidentialPool()) return this.residentialPoolText();
    return 'No Pool';
  }

  waterSourceText(): string | null {
    const waterSource = this.details.waterSource;
    if (isEmpty(waterSource)) return null;

    switch (waterSource) {
      case 'Municipal':
      case 'Well':
        return waterSource + ' Water';
      case 'None':
        return 'No Water';
      case 'Other':
        return 'Other Water';
      case 'Both':
        return 'Municipal + Well Water';
      default:
        break;
    }

    return waterSource;
  }

  cloneWithUpdates(updates: RecursivePartial<FullListingObject>) {
    return new FullListing(deepCloneWithUpdates(this.toObject(), updates));
  }

  isMissingSoldDateOrPrice(): boolean {
    return this.soldDate === null || this.soldPrice === null;
  }

  yearsOldText(): null | string {
    if (isEmpty(this.details.yearBuilt)) {
      return null;
    }
    return this.details.yearBuilt + ' Years Old';
  }

  lotSizeText(): null | string {
    const { width, depth } = this.lot;
    if (!width || !depth) {
      return null;
    }
    if (width && depth) {
      const widthFeet = round(metresToFeet(width), 1);
      const depthFeet = round(metresToFeet(depth), 1);
      return `${widthFeet} x ${depthFeet} Feet`;
    } else {
      return null;
    }
  }

  showPriceChange(): boolean {
    if (!this.originalPrice) return false;
    if (this.isSoldOrLeased()) return false;
    return (
      this.hasPriceChange() ||
      this.lastStatus === RepliersListingLastStatus.PriceChanged
    );
  }

  hasPriceChange(): boolean {
    return Boolean(this.originalPrice) && this.originalPrice !== this.listPrice;
  }

  propertyTypeText(): string {
    return (
      PROPERTY_TYPE_DISPLAY_MAPPING[this.propertyType] || this.propertyType
    );
  }

  propertyTypesFilterOptions(): Array<PropertyTypeFilterOptionLabels> {
    const matchingFilterOptions = propertyTypeFilterOptions.filter(
      propertyTypeFilterOption => {
        return propertyTypeFilterOption.repliersPropertyTypesIncluded.includes(
          this.propertyType,
        );
      },
    );
    return matchingFilterOptions.map(
      propertyTypeFilterOption => propertyTypeFilterOption.label,
    );
  }

  previewTitle(): string {
    return `${this.address.formattedAddressShortOneLiner()}, ${
      this.address.neighborhood
    } | TopHouse`;
  }

  previewDescription(): string {
    return `${this.propertyTypeText()} | ${this.lastStatusText()} | ${this.displayPrice()} | ${this.bedroomsText(
      'Bed',
    )}, ${this.bathroomsText('Bath')} | ${this.parkingSpacesText()} | ${
      this.style
    }`;
  }

  toObject(): FullListingObject {
    return {
      id: this.id,
      propertyId: this.propertyId,
      mlsNumber: this.mlsNumber,
      listDate: this.listDate.toUTC().toString(),
      soldDate: this.soldDate?.toUTC().toString() || null,
      address: this.address.toObject(),
      coordinates: this.coordinates,
      resource: this.resource,
      listPrice: this.listPrice,
      soldPrice: this.soldPrice,
      originalPrice: this.originalPrice,
      lastStatus: this.lastStatus,
      status: this.status,
      images: this.images,
      blurhashes: this.blurhashes,
      blurhashesUpdatedAt: this.blurhashesUpdatedAt?.toUTC().toString() || null,
      details: this.details.toObject(),
      taxes: this.taxes,
      brokerageName: this.brokerageName,
      transactionType: this.transactionType,
      buildingClass: this.buildingClass,
      propertyType: this.propertyType,
      style: this.style,
      lot: this.lot.toObject(),
      timestamps: {
        expiryDate: this.timestamps.expiryDate?.toUTC().toString() || null,
        terminatedDate:
          this.timestamps.terminatedDate?.toUTC().toString() || null,
        listingEntryDate:
          this.timestamps.listingEntryDate?.toUTC().toString() || null,
        closedDate: this.timestamps.closedDate?.toUTC().toString() || null,
        possessionDate:
          this.timestamps.possessionDate?.toUTC().toString() || null,
        idxUpdatedDate:
          this.timestamps.idxUpdatedDate?.toUTC().toString() || null,
        conditionalExpiryDate:
          this.timestamps.conditionalExpiryDate?.toUTC().toString() || null,
        listingUpdatedDate:
          this.timestamps.listingUpdatedDate?.toUTC().toString() || null,
        photosUpdatedDate:
          this.timestamps.photosUpdatedDate?.toUTC().toString() || null,
        suspendedDate:
          this.timestamps.suspendedDate?.toUTC().toString() || null,
        extensionEntryDate:
          this.timestamps.extensionEntryDate?.toUTC().toString() || null,
        unavailableDate:
          this.timestamps.unavailableDate?.toUTC().toString() || null,
      },
      condo: this.condo?.toObject() || null,
      rooms: this.rooms,
      permissions: this.permissions,
      mlsUpdatedOn: this.mlsUpdatedOn.toUTC().toString(),
      daysOnMarketFromMls: this.daysOnMarketFromMls,
      repliersUpdatedOn: this.repliersUpdatedOn?.toUTC().toString() || null,
      openHouses: this.openHouses
        ? this.openHouses.map(openHouse => openHouse.toObject())
        : null,
      similarSolds: this.similarSolds,
      similarSoldsUpdatedAt:
        this.similarSoldsUpdatedAt?.toUTC().toString() || null,
      offerDate: this.offerDate?.toUTC().toString() || null,
      isOfferDateProcessed: this.isOfferDateProcessed,
      offerCount: this.offerCount,
      dateBuiltRange: this.dateBuiltRange,
      isFsbo: this.isFsbo,
      isPowerOfSale: this.isPowerOfSale,
      sqftRange: this.sqftRange,
      lastGeocodedAt: this.lastGeocodedAt?.toUTC().toString() || null,
    };
  }

  toListingRecord(): FullListingRecord {
    //TODO: is there a more elegant way to do this? (remove these fields from details)
    const {
      basement1,
      basement2,
      description,
      numBathrooms,
      numBathroomsPlus,
      numBedrooms,
      numBedroomsPlus,
      numGarageSpaces,
      numParkingSpaces,
      sqft,
      balcony,
      ...listingRecordDetails
    } = this.details.toObject();
    return {
      ...instanceToPlain(this),
      listDate: this.listDate.toUTC().toString(),
      soldDate: this.soldDate ? this.soldDate?.toUTC().toString() : null,
      blurhashesUpdatedAt: this.blurhashesUpdatedAt
        ? this.blurhashesUpdatedAt.toUTC().toString()
        : null,
      details: listingRecordDetails,
      basement1: this.details.basement1,
      basement2: this.details.basement2,
      description: this.details.description,
      numBathrooms: this.details.numBathrooms,
      numBathroomsPlus: this.details.numBathroomsPlus,
      numBedrooms: this.details.numBedrooms,
      numBedroomsPlus: this.details.numBedroomsPlus,
      numGarageSpaces: this.details.numGarageSpaces,
      numParkingSpaces: this.details.numParkingSpaces,
      sqft: this.details.sqft,
      propertyType: this.propertyType,
      style: this.style,
      balcony: this.details.balcony,
      condoExposure: this.condo?.exposure || null,
      ...this.lot.toListingLotRecord(),
      ...this.timestamps,
      condo: this.condo?.toListingCondoDetailsRecord(),
      rooms: this.rooms,
      permissionsCanDisplayAddress: this.permissions.canDisplayAddress,
      permissionsCanDisplayPublic: this.permissions.canDisplayPublic,
      mlsUpdatedOn: this.mlsUpdatedOn.toUTC().toString(),
      daysOnMarketFromMls: this.daysOnMarketFromMls,
      repliersUpdatedOn: this.repliersUpdatedOn
        ? this.repliersUpdatedOn.toUTC().toString()
        : null,
      openHouses: this.openHouses
        ? this.openHouses.map(openHouse => openHouse.toObject())
        : null,
      similarSoldsUpdatedAt: this.similarSoldsUpdatedAt
        ? this.similarSoldsUpdatedAt.toUTC().toString()
        : null,
      offerDate: this.offerDate ? this.offerDate.toUTC().toString() : null,
      dateBuiltRange: this.dateBuiltRange,
      sqftRange: this.sqftRange,
      lastGeocodedAt: this.lastGeocodedAt
        ? this.lastGeocodedAt?.toUTC().toString()
        : null,
    } as unknown as FullListingRecord;
  }
}

export { FullListing };
